import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";

import {Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    BodyText,
    LabelLarge,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {LINKED_ACCOUNTS} from "../constants.ts";

import {SET_PRIMARY_EMAIL_MUTATION} from "./set-primary-email-mutation.ts";

const constructClassroomLink = (pathname: string): string => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const userType = pathSegments[0]; // 'learner' or 'teacher'
    return `/${userType}/settings/account#${LINKED_ACCOUNTS}`;
};

const strings = {
    connectEmailHere: (connect: React.ReactNode) => (
        <Trans>To use an email address not listed here, {connect}.</Trans>
    ),
} as const;

interface Props {
    email: string;
    authEmails: Array<string | null | undefined>;
}

export const PrimaryEmailSection: React.FC<Props> = ({email, authEmails}) => {
    const {entrypoint} = React.useContext(AppShellContext);
    const isKhanClassroom = entrypoint === "classroom";
    const location = useLocation();
    const [selectedEmail, setSelectedEmail] = useState(email);
    const [saving, setSaving] = useState(false);
    const [updateEmail] = useMutation(SET_PRIMARY_EMAIL_MUTATION);

    // Track component mounted state to prevent state updates after unmount
    const [isMounted, setIsMounted] = useState(true);
    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    const handleChange = async (selected: string) => {
        if (!isMounted) {
            return;
        }

        const oldEmail = selectedEmail;
        setSelectedEmail(selected);
        setSaving(true);

        try {
            const result = await updateEmail({
                email: selected,
            });

            if (isMounted) {
                if (
                    result.status === "success" &&
                    result.data?.setPrimaryEmail?.error
                ) {
                    setSelectedEmail(oldEmail);
                } else {
                    setSelectedEmail(selected);
                }
            }
        } catch (error) {
            if (isMounted) {
                setSelectedEmail(oldEmail);
            }
        } finally {
            if (isMounted) {
                setSaving(false);
            }
        }
    };

    const renderEmailSelect = (emailSelectId: string) => {
        return (
            <SingleSelect
                onChange={handleChange}
                placeholder=""
                selectedValue={selectedEmail}
                id={emailSelectId}
                disabled={saving}
            >
                {authEmails.map(
                    (email) =>
                        !!email && (
                            <OptionItem
                                label={email}
                                value={email}
                                key={email}
                            />
                        ),
                )}
            </SingleSelect>
        );
    };

    const renderSection = (idRoot: string) => {
        const emailSelectId = `${idRoot}email`;

        const linkedAccountsLink = isKhanClassroom
            ? constructClassroomLink(location.pathname)
            : `/settings/account#${LINKED_ACCOUNTS}`;
        const connect = (
            <Link href={linkedAccountsLink} inline>
                {t`connect a new email to your account`}
            </Link>
        );

        return (
            <View role="region" aria-label={t`Primary email settings`}>
                <View style={styles.row}>
                    <LabelMedium
                        htmlFor={emailSelectId}
                        style={[styles.label, styles.verticalCenter]}
                        tag="label"
                    >
                        {t`Send email to`}
                    </LabelMedium>
                    <View style={{width: sizing.size_160}} />
                    {authEmails.length > 1 ? (
                        renderEmailSelect(emailSelectId)
                    ) : (
                        <LabelLarge id={emailSelectId}>
                            {selectedEmail}
                        </LabelLarge>
                    )}
                    {saving && (
                        <View style={{marginInlineStart: sizing.size_160}}>
                            <CircularSpinner size="small" testId="spinner" />
                        </View>
                    )}
                </View>
                <View style={{marginBlockStart: sizing.size_160}}>
                    <BodyText>{strings.connectEmailHere(connect)}</BodyText>
                </View>
            </View>
        );
    };

    return <Id>{(idRoot) => renderSection(idRoot)}</Id>;
};

const styles = StyleSheet.create({
    label: {
        color: semanticColor.core.foreground.neutral.strong,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_160,
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
});

export default PrimaryEmailSection;
